(() => {
  'use strict';
  if (window.mpAttributionLoaded) return;
  window.mpAttributionLoaded = true;

  const STORAGE_KEY = 'mp_attr_v1';
  const CART_KEY = 'mp_attr_cart_v1';
  // Refs whose consent withdrawal has not been confirmed by the app yet (6-character aliases only,
  // no identifiers): keeping them is strictly necessary to honour the withdrawal if the network fails.
  const REVOKE_KEY = 'mp_attr_revoke_v1';
  const MAX_AGE = 90 * 24 * 60 * 60 * 1000;
  const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'srsltid'];
  const PAID_KEYS = ['gclid', 'gbraid', 'wbraid'];
  const CLICK_KEYS = [...PAID_KEYS, 'msclkid', 'srsltid'];
  const VALUE_TRACK_KEYS = {
    campaign_id: 'campaignid', ad_group_id: 'adgroupid', offer_id: 'product_id', target_id: 'targetid',
    keyword_text: 'keyword', match_type: 'matchtype', device: 'device', network: 'network',
  };
  const CART_FIELDS = { _mp_ref: 'ref', _gclid: 'gclid', _gbraid: 'gbraid', _wbraid: 'wbraid', _msclkid: 'msclkid' };
  const clean = value => typeof value === 'string' ? value.replace(/[^A-Za-z0-9._\-~ ]/g, '').slice(0, 120) : '';
  const consentToken = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value) ? value : '';
  const validRef = value => typeof value === 'string' && /^[A-HJ-NP-Z2-9]{6}$/.test(value) ? value : '';
  const internalTraffic = window.navigator?.webdriver === true || new URLSearchParams(window.location.search).get('prova') === '1';
  const allowed = () => window.mpPrivacy?.marketingAllowed() === true;
  const storageGet = key => { try { return window.localStorage.getItem(key); } catch (_) { return null; } };
  const storageSet = (key, value) => { try { window.localStorage.setItem(key, value); return true; } catch (_) { return false; } };
  const storageRemove = key => { try { window.localStorage.removeItem(key); } catch (_) {} };
  const tokenId = token => typeof token === 'string' ? token.split('?')[0] : '';
  function cartToken() {
    try { return tokenId(decodeURIComponent((document.cookie || '').match(/(?:^|;\s*)cart=([^;]*)/)?.[1] || '')); }
    catch (_) { return ''; }
  }
  const cartMarker = (token, value) => JSON.stringify({ token, value });

  function origin(value) {
    try {
      const url = new URL(value);
      return /^https?:$/.test(url.protocol) ? url.origin.slice(0, 120) : '';
    } catch (_) { return ''; }
  }
  function path(value) {
    return typeof value === 'string' ? value.split(/[?#]/, 1)[0].replace(/[^A-Za-z0-9._\-~ /]/g, '').slice(0, 120) : '';
  }
  function valueTrack(touch, params) {
    const result = {};
    if (!PAID_KEYS.some(key => clean(touch[key]).trim())) return result;
    Object.entries(VALUE_TRACK_KEYS).forEach(([key, alias]) => {
      const normalize = value => ['offer_id', 'keyword_text'].includes(key)
        ? (typeof value === 'string' ? value.trim().slice(0, 200) : '') : clean(value).trim();
      const value = normalize(params?.get(key)) || normalize(params?.get(alias)) || normalize(touch[key]);
      if (value) result[key] = value;
    });
    return result;
  }
  function buildClickPayload(touch, params, consent, saved) {
    const payload = {};
    CAMPAIGN_KEYS.filter(key => key !== 'fbclid').forEach(key => {
      const value = clean(touch[key]).trim();
      if (value) payload[key] = value;
    });
    Object.assign(payload, valueTrack(touch, params));
    const landing = path(touch.landing_page);
    payload.landing_path = landing.startsWith('/') && !landing.startsWith('//') ? landing : '/';
    try {
      const host = new URL(touch.referrer).hostname;
      if (host.length <= 253 && /^[a-zA-Z0-9.-]+$/.test(host)) payload.referrer_host = host;
    } catch (_) {}
    const ref = validRef(saved?.ref) || validRef(touch.ref);
    if (ref) {
      payload.ref = ref;
      const token = consentToken(saved?.consent_token) || consentToken(touch.consent_token);
      if (token) payload.consent_token = token;
    }
    payload.consent_ad_user_data = consent === true ? 'granted' : 'denied';
    payload.consent_ad_personalization = payload.consent_ad_user_data;
    return payload;
  }
  function validTouch(touch) {
    if (!touch || typeof touch.timestamp !== 'string') return null;
    const time = Date.parse(touch.timestamp), age = Date.now() - time;
    if (!Number.isFinite(time) || age < 0 || age >= MAX_AGE) return null;
    const result = { timestamp: new Date(time).toISOString(), landing_page: path(touch.landing_page), referrer: origin(touch.referrer) };
    [...CAMPAIGN_KEYS, 'ref'].forEach(key => { const value = clean(touch[key]); if (value) result[key] = value; });
    Object.assign(result, valueTrack(touch));
    if (validRef(result.ref) && consentToken(touch.consent_token)) result.consent_token = touch.consent_token;
    if (touch._kx === true) result._kx = true;
    return result;
  }
  function read() {
    try {
      const saved = JSON.parse(storageGet(STORAGE_KEY));
      return { first: validTouch(saved?.first), last: validTouch(saved?.last) };
    } catch (_) { return null; }
  }

  // Bound both fetch and JSON parsing. A failed attribution must never block the storefront.
  async function post(url, body, external = false, keepalive = !external) {
    if (typeof fetch !== 'function') return null;
    const controller = new AbortController();
    let timer;
    try {
      return await Promise.race([
        (async () => {
          const response = await fetch(url, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body), signal: controller.signal, keepalive,
            credentials: external ? 'omit' : 'same-origin', referrerPolicy: 'no-referrer',
          });
          if (!response.ok) return null;
          return await response.json();
        })(),
        new Promise(resolve => { timer = setTimeout(() => { controller.abort(); resolve(null); }, 4000); }),
      ]);
    } catch (_) { return null; }
    finally { clearTimeout(timer); }
  }

  let initialized = false, generation = 0, attribution = null, canReadStorage = false;
  let cartQueue = Promise.resolve(), lastCartValue = null, cleanupQueued = false;
  const emptyAttributes = () => Object.fromEntries(Object.keys(CART_FIELDS).map(key => [key, '']));
  function syncCart(touch, clear = false) {
    const attributes = emptyAttributes();
    if (!clear) Object.entries(CART_FIELDS).forEach(([key, field]) => { attributes[key] = clean(touch?.[field]); });
    const value = JSON.stringify(attributes);
    cartQueue = cartQueue.catch(() => {}).then(async () => {
      if (!clear && (internalTraffic || !allowed())) return;
      const token = cartToken(), marker = cartMarker(token, value);
      if (!clear && token && (lastCartValue === marker || storageGet(CART_KEY) === marker)) return;
      // A direct visit with no identifiers should not touch a clean cart.
      if (!clear && !Object.values(attributes).some(Boolean) && !storageGet(CART_KEY) && !lastCartValue) return;
      // Keep a dirty marker even if a write or erasure times out after reaching the cart.
      lastCartValue = null;
      storageSet(CART_KEY, cartMarker(token, null));
      const result = await post(`${window.Shopify?.routes?.root || '/'}cart/update.js`, { attributes });
      if (!result) return;
      if (clear) { lastCartValue = null; storageRemove(CART_KEY); }
      else if (allowed()) {
        lastCartValue = cartMarker(tokenId(result.token) || token || cartToken(), value);
        storageSet(CART_KEY, lastCartValue);
      }
    });
    return cartQueue;
  }
  function persist() { canReadStorage = storageSet(STORAGE_KEY, JSON.stringify(attribution)); }
  window.mpAttribution = () => {
    if (!allowed()) return null;
    const latest = canReadStorage ? read() : null;
    if (latest?.first || latest?.last) attribution = latest;
    const first = validTouch(attribution?.first), last = validTouch(attribution?.last);
    return first || last ? { first, last } : null;
  };

  const clickEndpoint = () => document.querySelector?.('[data-mp-ad-click-endpoint]')?.dataset.mpAdClickEndpoint
    || 'https://mundoparquet-app.vercel.app/api/public/ad-click';
  async function registerClick(touch, params, currentGeneration) {
    const payload = buildClickPayload(touch, params, allowed(), touch);
    // Campaign detection also includes fbclid/UTM-only URLs that cannot identify an app click.
    if (!payload.ref && !CLICK_KEYS.some(key => payload[key]) && !payload.utm_source) {
      await syncCart(attribution.last || attribution.first);
      return;
    }
    const response = await post(clickEndpoint(), payload, true);
    if (!allowed() || generation !== currentGeneration) return;
    const ref = response?.error || response?.ok === false ? '' : clean(response?.ref_code);
    // A newer tab's campaign must not be overwritten, including on network failure.
    const saved = canReadStorage ? read() : null;
    if (saved?.first || saved?.last) attribution = saved;
    if (ref) {
      // The app answers with a fresh alias of the SAME click whenever a ref was sent, so the token
      // (issued per click) stays valid; without a ref sent, only a token in the response counts.
      const token = consentToken(response?.consent_token) || (payload.ref ? consentToken(touch.consent_token) : '');
      for (const key of ['first', 'last']) {
        const candidate = attribution[key];
        if (candidate && Object.keys(candidate).length === Object.keys(touch).length &&
            Object.keys(touch).every(field => candidate[field] === touch[field])) {
          attribution[key] = { ...attribution[key], ref };
          if (token) attribution[key].consent_token = token;
          else delete attribution[key].consent_token;
        }
      }
      persist();
    }
    await syncCart(attribution.last || attribution.first);
  }
  function initialize() {
    if (!allowed()) { withdraw(); return; }
    if (internalTraffic) return;
    if (initialized || !window.mpPrivacy.registerResource('attribution', ['marketing'])) return;
    initialized = true;
    cleanupQueued = false;
    const currentGeneration = ++generation;
    const params = new URLSearchParams(window.location.search);
    const touch = { timestamp: new Date().toISOString(), landing_page: path(window.location.pathname), referrer: origin(document.referrer) };
    const isCampaign = CAMPAIGN_KEYS.some(key => params.has(key));
    CAMPAIGN_KEYS.forEach(key => { const value = clean(params.get(key)); if (value) touch[key] = value; });
    Object.assign(touch, valueTrack(touch, params));
    if (params.has('_kx')) touch._kx = true;
    const saved = read();
    // Reuse ownership only for the same click/campaign; a new paid click needs its own ref.
    // A shared srsltid never lets a paid click inherit the free Shopping capture.
    const matchKeys = PAID_KEYS.some(key => touch[key]) ? PAID_KEYS : CLICK_KEYS;
    const previous = [saved?.last, saved?.first].find(candidate => validRef(candidate?.ref) && (
      matchKeys.some(key => touch[key] && touch[key] === candidate[key]) ||
      (!CLICK_KEYS.some(key => touch[key] || candidate[key]) &&
        CAMPAIGN_KEYS.every(key => (touch[key] || '') === (candidate[key] || '')))
    ));
    if (previous) {
      touch.ref = previous.ref;
      if (previous.consent_token) touch.consent_token = previous.consent_token;
    }
    attribution = { first: saved?.first || touch, last: isCampaign ? touch : saved?.last || touch };
    persist();
    if (isCampaign) void registerClick(touch, params, currentGeneration);
    else void syncCart(attribution.last || attribution.first);
  }

  // Delegation covers the floating button, static links and dynamically rendered door sharing links.
  const whatsappLinks = new Map();
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    try {
      const url = new URL(link.href, window.location.href);
      if (!((url.protocol === 'https:' && ['wa.me', 'api.whatsapp.com', 'web.whatsapp.com'].includes(url.hostname)) ||
          (url.protocol === 'whatsapp:' && url.hostname === 'send')) || !url.searchParams.has('text')) return;
      const previous = whatsappLinks.get(link);
      // A component may have regenerated its message since the last click.
      const base = previous?.decorated === link.href ? previous.base : link.href;
      const target = new URL(base, window.location.href);
      const current = window.mpAttribution();
      const ref = clean((current?.last || current?.first)?.ref);
      if (allowed() && ref) target.searchParams.set('text', `${target.searchParams.get('text')}\n(Ref: ${ref})`);
      link.href = target.href;
      whatsappLinks.set(link, { base, decorated: link.href });
    } catch (_) { /* Keep the original WhatsApp link usable. */ }
  }, true);

  function pendingRevocations() {
    try { const list = JSON.parse(storageGet(REVOKE_KEY) || '[]'); return Array.isArray(list) ? list.filter(validRef) : []; } catch (_) { return []; }
  }
  function setPendingRevocations(refs) {
    if (refs.length) storageSet(REVOKE_KEY, JSON.stringify(refs)); else storageRemove(REVOKE_KEY);
  }
  // Lowering consent needs only the ref; the entry is cleared once the app has confirmed it.
  function revoke(ref, token) {
    const payload = { ref, consent_ad_user_data: 'denied', consent_ad_personalization: 'denied' };
    if (token) payload.consent_token = token;
    return post(clickEndpoint(), payload, true, true).then(result => {
      // Only an explicit success from the app clears the entry; an error body or a failed request keeps it.
      const confirmed = result !== null && typeof result === 'object' && !result.error && result.ok !== false;
      if (confirmed) setPendingRevocations(pendingRevocations().filter(pending => pending !== ref));
      return result;
    });
  }
  function retryRevocations() {
    return Promise.all(pendingRevocations().map(ref => revoke(ref, '')));
  }
  function withdraw() {
    const wasActive = initialized;
    initialized = false; generation++;
    // Storage is only touched here, where the controller already denies marketing: pending
    // withdrawals from an earlier failed request are retried together with the current ones.
    const saved = read(), refs = new Map();
    for (const ref of pendingRevocations()) refs.set(ref, '');
    for (const touch of [saved?.first, saved?.last, attribution?.first, attribution?.last]) {
      const ref = validRef(touch?.ref);
      if (ref && !refs.get(ref)) refs.set(ref, consentToken(touch.consent_token));
    }
    setPendingRevocations([...refs.keys()]);
    const revocations = [...refs].map(([ref, token]) => revoke(ref, token));
    attribution = null; canReadStorage = false;
    storageRemove(STORAGE_KEY);
    for (const [link, previous] of whatsappLinks) {
      if (link.href === previous.decorated) link.href = previous.base;
    }
    whatsappLinks.clear();
    // Retry legacy or dirty markers once per page; new visitors need no cart request.
    if (cleanupQueued || (!wasActive && !storageGet(CART_KEY) && !lastCartValue)) {
      if (revocations.length) window.mpAttributionCleanup = Promise.all([window.mpAttributionCleanup, ...revocations]);
      return;
    }
    cleanupQueued = true;
    // The privacy controller waits for the queued erasure before its mandatory reload.
    window.mpAttributionCleanup = Promise.all([syncCart(null, true), ...revocations]);
  }
  document.addEventListener('mp:privacy-changed', () => {
    if (!allowed()) withdraw();
    else initialize();
  });
  document.addEventListener('cart:change', () => {
    if (initialized && allowed()) void syncCart(attribution.last || attribution.first);
  });
  initialize();
})();
